import { Show } from 'solid-js';
import { Section } from '../../grid-system/section/section';
import { Heading } from '../../ui-components/heading/heading';
import { Button } from '../../ui-components/button/button';
import { ButtonProps } from '../../ui-components/button/button-types';
import { ErrorCatcher } from '../../tools/error-catcher';
import { useHeroImageBuilder } from '../hero-cvi/image-helpers';
import { HeroComponentProps } from './hero-campaign-types';

/**
 * Widths for the full-bleed image's `srcset`. Every entry MUST exist in the images
 * service's `ALLOWED_SIZES` env var — an unlisted width 404s and the service serves a
 * "WRONG SIZE!" placeholder instead of the image. The figure is capped at 1800px CSS,
 * so 1920/2400 are here to cover high-DPI screens rather than wider layouts.
 */
const IMAGE_WIDTHS = [ 600, 750, 1000, 1200, 1500, 1920, 2400 ] as const;
const IMAGE_SIZES = '(min-width: 1800px) 1800px, 100vw';

/**
 * Campaign hero in the CVI "large image hero" layout: the copy sits in a page-header
 * block *above* a full-bleed image rather than on top of it. The CVI system never sets
 * bare copy on a photograph — where it does overlay the image it does so with an opaque
 * card — so the previous full-viewport background-image treatment has no CVI equivalent.
 *
 * Aspect ratios follow the CVI block (2/1 desktop, 7/4 tablet, fixed band on mobile),
 * mapped onto the nearest existing breakpoint tokens rather than new arbitrary ones.
 */
export const HeroCampaign = (props: HeroComponentProps) => {
    const { buildImageSrc, buildImageSrcSet, imageStyle } = useHeroImageBuilder();

    const hasCaption = () => Boolean(props.captionName || props.captionRole || props.captionText);

    const image = () => {
        if (!props.src) {
            return undefined;
        }

        return {
            url: props.src,
            sign: props.sign,
            focus: props.focus,
            altText: props.altText,
        };
    };

    return (
        <ErrorCatcher componentName="HeroCampaign">
            <div class="bg-cvi-bg-block-default">
                <Section
                    templateShorthand={[ 12 ]}
                    widthType="bgFull"
                    removeSpacingBlock={true}
                    customCss="padding-top: 0 !important; padding-bottom: 0 !important; background: transparent;"
                >
                    <div class="flex flex-col gap-cvi-5 py-cvi-section-default text-cvi-fg-default tablet-original:gap-cvi-7">
                        <Show when={props.largerHeading}>
                            <Heading tag="h1" variant="hero" noBlockSpacing class="text-balance">
                                {`${props.largerHeading}<span class="text-cvi-fg-brand" aria-hidden="true">_</span>`}
                            </Heading>
                        </Show>

                        <Show when={props.description}>
                            <p class="m-0 max-w-[55ch] pb-cvi-7 heading-xl font-cvi-regular text-pretty text-cvi-fg-muted">
                                {props.description}
                            </p>
                        </Show>

                        <Show when={props.customLink?.url}>
                            <div>
                                <Button
                                    label={props.customLink.label || ''}
                                    url={props.customLink.url}
                                    target={props.customLink.target}
                                    variant={(props.customLink.variant as ButtonProps['variant']) || 'primary'}
                                    isLink={true}
                                />
                            </div>
                        </Show>
                    </div>
                </Section>

                <Show when={image()}>
                    <figure class="m-0">
                        <div class="mx-auto w-full max-w-[1800px]">
                            <img
                                src={buildImageSrc(image(), 1200)}
                                srcset={buildImageSrcSet(image(), IMAGE_WIDTHS)}
                                sizes={IMAGE_SIZES}
                                alt={props.altText ?? ''}
                                class={
                                    'block w-full h-cvi-hero-band object-cover '
                                    + 'tablet-original:h-auto tablet-original:aspect-[7/4] '
                                    + 'desktop:aspect-[2/1] '
                                    + 'min-[1800px]:rounded-cvi-md'
                                }
                                style={imageStyle(image())}
                                {...!props.altText ? { role: 'presentation' } : {}}
                            />
                        </div>

                        <Show when={hasCaption()}>
                            <figcaption>
                                <Section
                                    templateShorthand={[ 12 ]}
                                    widthType="bgFull"
                                    removeSpacingBlock={true}
                                    customCss="padding-top: 0 !important; padding-bottom: 0 !important; background: transparent;"
                                >
                                    {/* Same shape as the caption in `hero-cvi/single-image.tsx`
                                        (name | role • text), so the two heroes stay identical. */}
                                    <p class="m-0 py-cvi-5 body-sm font-cvi-regular text-cvi-fg-default">
                                        <Show when={props.captionName}>
                                            <span class="font-cvi-medium">{props.captionName}</span>
                                        </Show>
                                        <Show when={props.captionName && props.captionRole}>
                                            <span aria-hidden="true">{' | '}</span>
                                            <span class="sr-only">, </span>
                                        </Show>
                                        <Show when={props.captionRole}>
                                            <span>{props.captionRole}</span>
                                        </Show>
                                        <Show when={(props.captionName || props.captionRole) && props.captionText}>
                                            <span
                                                class="inline-block size-cvi-2 mx-cvi-4 rounded-cvi-round bg-cvi-fg-brand align-middle"
                                                aria-hidden="true"
                                            />
                                            <span class="sr-only">. </span>
                                        </Show>
                                        <Show when={props.captionText}>
                                            <span class="text-cvi-fg-muted">{props.captionText}</span>
                                        </Show>
                                    </p>
                                </Section>
                            </figcaption>
                        </Show>
                    </figure>
                </Show>
            </div>
        </ErrorCatcher>
    );
};

HeroCampaign.parseProps = (atts: any) => {
    const { image, customLink } = atts;

    return {
        src: image?.url,
        altText: image?.altText,
        focus: image?.focus,
        sign: image?.sign,
        largerHeading: atts.largerHeading,
        description: atts.description,
        customLink: customLink ? customLink[ 0 ] : null,
        captionName: atts.captionName,
        captionRole: atts.captionRole,
        captionText: atts.captionText,
    };
};
